import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Warehouse, TrendingUp, Package, Zap, Shield, Globe, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { customers, services, stats } from '../mock';

const Home = () => {
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex((prev) => (prev + 1) % customers.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      Warehouse: Warehouse,
      TrendingUp: TrendingUp,
      Package: Package,
      Zap: Zap,
      Shield: Shield,
      Globe: Globe,
      AlertTriangle: AlertTriangle
    };
    const Icon = icons[iconName] || Package;
    return <Icon className="h-8 w-8" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-visible">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 -z-10"></div>
        
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-slate-100 px-4 py-2 rounded-full">
                <Shield className="h-4 w-4 text-slate-700" />
                <span className="text-sm font-medium text-slate-700">TSA Certified Operations</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight pl-0.5">
                Supply Chain
                <span className="block bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mt-2 pb-2">
                  Logistics Solutions
                </span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                Trusted logistics partner with field stocking locations across America. We provide comprehensive supply chain management, warehousing, fulfillment, and rush delivery services.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-slate-800 to-slate-600 hover:from-slate-700 hover:to-slate-500 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/tracking">
                  <Button variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg rounded-lg transition-all duration-300">
                    Track Shipment
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-6 pt-8">
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} className="space-y-1">
                    <div className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1644079446600-219068676743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzY3MjYyOTg3fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern Warehouse"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-slate-200">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">99.8%</div>
                    <div className="text-sm text-slate-600">On-Time Delivery</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Showcase */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Trusted by Industry Leaders</h2>
            <p className="text-slate-600">Serving Fortune 500 companies and government agencies nationwide</p>
          </div>
          
          <div className="relative overflow-hidden">
            {/* Row 1 - Scrolling right (first 27 customers) */}
            <div className="flex w-max animate-scroll-fast mb-6">
              {[...customers.slice(0, 27), ...customers.slice(0, 27)].map((customer, index) => (
                <div
                  key={`row1-${customer.name}-${index}`}
                  className="flex-shrink-0 w-40 h-20 mr-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all duration-300 px-4"
                >
                  <img
                    src={customer.logo}
                    alt={customer.name}
                    className="max-w-[120px] max-h-[50px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-semibold text-slate-600">${customer.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
            
            {/* Row 2 - Scrolling left (customers 27-54) */}
            <div className="flex w-max animate-scroll-reverse mb-6">
              {[...customers.slice(27, 54), ...customers.slice(27, 54)].map((customer, index) => (
                <div
                  key={`row2-${customer.name}-${index}`}
                  className="flex-shrink-0 w-40 h-20 mr-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all duration-300 px-4"
                >
                  <img
                    src={customer.logo}
                    alt={customer.name}
                    className="max-w-[120px] max-h-[50px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-semibold text-slate-600">${customer.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
            
            {/* Row 3 - Scrolling right (customers 54-81) */}
            <div className="flex w-max animate-scroll-fast mb-6">
              {[...customers.slice(54, 81), ...customers.slice(54, 81)].map((customer, index) => (
                <div
                  key={`row3-${customer.name}-${index}`}
                  className="flex-shrink-0 w-40 h-20 mr-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all duration-300 px-4"
                >
                  <img
                    src={customer.logo}
                    alt={customer.name}
                    className="max-w-[120px] max-h-[50px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-semibold text-slate-600">${customer.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
            
            {/* Row 4 - Scrolling left (customers 81-108) */}
            <div className="flex w-max animate-scroll-reverse mb-6">
              {[...customers.slice(81, 108), ...customers.slice(81, 108)].map((customer, index) => (
                <div
                  key={`row4-${customer.name}-${index}`}
                  className="flex-shrink-0 w-40 h-20 mr-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all duration-300 px-4"
                >
                  <img
                    src={customer.logo}
                    alt={customer.name}
                    className="max-w-[120px] max-h-[50px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-semibold text-slate-600">${customer.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
            
            {/* Row 5 - Scrolling right (remaining customers) */}
            <div className="flex w-max animate-scroll-fast">
              {[...customers.slice(108), ...customers.slice(108)].map((customer, index) => (
                <div
                  key={`row5-${customer.name}-${index}`}
                  className="flex-shrink-0 w-40 h-20 mr-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all duration-300 px-4"
                >
                  <img
                    src={customer.logo}
                    alt={customer.name}
                    className="max-w-[120px] max-h-[50px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-semibold text-slate-600">${customer.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Comprehensive Logistics Services</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From field stocking locations to international shipping, we deliver excellence at every stage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="p-8 hover:shadow-xl transition-all duration-300 border-slate-200 bg-white group cursor-pointer">
                <div className="bg-gradient-to-br from-slate-100 to-slate-50 group-hover:from-slate-800 group-hover:to-slate-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300">
                  <div className="text-slate-700 group-hover:text-white transition-colors duration-300">
                    {getIcon(service.icon)}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Sabey Logistics?</h2>
              <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                With multiple field stocking locations across the United States and a commitment to excellence, we ensure your supply chain runs smoothly and efficiently.
              </p>
              
              <div className="space-y-4">
                {[
                  'Strategic field stocking locations nationwide',
                  'TSA certified secure operations',
                  'Rush delivery for time-critical shipments',
                  '99.8% on-time delivery rate',
                  'Advanced inventory management',
                  'Dedicated account management'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                    <span className="text-slate-200">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1761195696590-3490ea770aa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxzdXBwbHklMjBjaGFpbiUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzY3MjYyOTkyfDA&ixlib=rb-4.1.0&q=85"
                alt="Supply Chain Technology"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Ready to Optimize Your Supply Chain?
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Get in touch with our logistics experts today and discover how we can help your business thrive.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-slate-800 to-slate-600 hover:from-slate-700 hover:to-slate-500 text-white px-10 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;